/**
 * @fileoverview Utility functions for programmatic scrolling using Lenis smooth scroll library.
 * These functions provide a consistent API for scroll operations with fallback to native scroll.
 */

/**
 * Scrolls to a specific target element or position using Lenis smooth scroll.
 *
 * @description This function provides programmatic scrolling with Lenis integration.
 * Falls back to native smooth scrolling if Lenis is not available.
 *
 * @param {string | number} target - CSS selector string or numeric pixel position
 * @param {Object} [options] - Optional scroll configuration
 * @param {number} [options.offset=0] - Pixel offset from target position
 * @param {number} [options.duration=1.2] - Animation duration in seconds
 *
 * @example
 * ```typescript
 * // Scroll to element with ID
 * scrollTo('#section-about', { offset: -100, duration: 1.5 });
 *
 * // Scroll to specific pixel position
 * scrollTo(500, { duration: 0.8 });
 * ```
 */
export const scrollTo = (
  target: string | number,
  options?: { offset?: number; duration?: number },
) => {
  const lenis = window.lenis;

  if (!lenis) {
    console.warn('Lenis no está inicializado');
    // Native scroll fallback when Lenis is unavailable
    if (typeof target === 'string') {
      const element = document.querySelector(target);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
    return;
  }

  // Use Lenis for smooth scrolling
  if (typeof target === 'string') {
    lenis.scrollTo(target, {
      offset: options?.offset || 0,
      duration: options?.duration || 1.2,
    });
  } else {
    lenis.scrollTo(target, {
      duration: options?.duration || 1.2,
    });
  }
};

/**
 * Smoothly scrolls to the top of the page.
 *
 * @param {number} [duration=1.2] - Animation duration in seconds
 *
 * @example
 * ```typescript
 * // Quick scroll to top
 * scrollToTop(0.5);
 * ```
 */
export const scrollToTop = (duration = 1.2) => {
  scrollTo(0, { duration });
};

/**
 * Scrolls to a specific section by ID with navbar offset consideration.
 *
 * @description Helper function for navigation menu items that accounts for fixed navbar height.
 *
 * @param {string} sectionId - The ID of the target section (without # prefix)
 * @param {number} [offset=-100] - Pixel offset to account for fixed navbar
 *
 * @example
 * ```typescript
 * // Scroll to about section with default offset
 * scrollToSection('about');
 *
 * // Scroll with custom offset
 * scrollToSection('contact', -120);
 * ```
 */
export const scrollToSection = (sectionId: string, offset = -100) => {
  scrollTo(`#${sectionId}`, { offset });
};
